import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2,
  FileText,
  Clock,
  CheckCircle2,
  LogOut,
  User,
  ChevronRight,
  ChevronLeft,
  Home,
  Calendar,
  MapPin,
  DollarSign,
  AlertCircle,
  Settings,
  Send,
  Search,
  SlidersHorizontal,
  ArrowUpDown,
  X,
  Eye,
  EyeOff,
  CreditCard,
  Loader2,
  ExternalLink,
  Globe,
  BarChart3,
} from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import OnboardingTour from "@/components/OnboardingTour";
import { useTranslation } from "react-i18next";
import { formatDate } from "@/utils/dateFormat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BusinessDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, logout } = useAuth();
  const [projects, setProjects] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("projects");
  const [showFilters, setShowFilters] = useState(false);
  const { t, i18n } = useTranslation();
  const [showTour, setShowTour] = useState(false);

  // Show onboarding tour for first-time contractors
  useEffect(() => {
    if (user && user.user_type === "business" && user.has_seen_tour === false) {
      setShowTour(true);
    }
  }, [user]);

  // Stripe Connect state
  const [connectStatus, setConnectStatus] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);

  // Pagination & filtering
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, total_pages: 0 });
  const [filters, setFilters] = useState({
    search: "",
    category_id: "",
    city: "",
    sort_by: "created_at",
    sort_order: "desc",
    hide_submitted: false
  });

  useEffect(() => {
    fetchCategories();
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  // Fetch Connect status when profile is approved
  useEffect(() => {
    if (profile?.is_approved) {
      fetchProjects();
      fetchEstimates();
      fetchConnectStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, filters, pagination.page]);

  // Handle return from Stripe onboarding
  useEffect(() => {
    if (searchParams.get("stripe_connected") === "true") {
      fetchConnectStatus();
      toast.success(t("business.stripeSetupUpdated"));
      searchParams.delete("stripe_connected");
      setSearchParams(searchParams, { replace: true });
    }
    if (searchParams.get("stripe_refresh") === "true") {
      toast.info(t("business.completeStripeSetup"));
      searchParams.delete("stripe_refresh");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories?lang=${i18n.language?.startsWith('es') ? 'es' : 'en'}`);
      setCategories(response.data);
    } catch (error) {
      console.error("Failed to fetch categories");
    }
  };

  const fetchProfile = async () => {
    try {
      const profileRes = await axios.get(`${API}/business/profile`);
      setProfile(profileRes.data);
    } catch (error) {
      if (error.response?.status === 404) {
        navigate("/business/profile");
        return;
      }
      console.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const fetchConnectStatus = async () => {
    try {
      const res = await axios.get(`${API}/connect/status`);
      setConnectStatus(res.data);
    } catch {
      setConnectStatus(null);
    }
  };

  const handleConnectOnboard = async () => {
    setConnectLoading(true);
    try {
      const res = await axios.post(`${API}/connect/onboard`, {}, {
        headers: { Origin: window.location.origin }
      });
      if (res.data.onboarding_url) {
        window.location.href = res.data.onboarding_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start payment setup");
    } finally {
      setConnectLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        sort_by: filters.sort_by,
        sort_order: filters.sort_order,
        hide_submitted: filters.hide_submitted.toString(),
      });

      if (filters.search) params.append("search", filters.search);
      if (filters.category_id) params.append("category_id", filters.category_id);
      if (filters.city) params.append("city", filters.city);

      const response = await axios.get(`${API}/projects/business?${params}`);
      setProjects(response.data.projects);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
        total_pages: response.data.total_pages
      }));
    } catch (error) {
      if (error.response?.status !== 403) {
        toast.error("Failed to load projects");
      }
    }
  };

  const fetchEstimates = async () => {
    try {
      const response = await axios.get(`${API}/estimates/business`);
      setEstimates(response.data);
    } catch (error) {
      console.error("Failed to load estimates");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      category_id: "",
      city: "",
      sort_by: "created_at",
      sort_order: "desc",
      hide_submitted: false
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const hasActiveFilters = filters.search || filters.category_id || filters.city || filters.hide_submitted;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Sidebar - Desktop */}
      <aside className="dashboard-sidebar hidden md:block">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-lg bg-[#D4AF37] flex items-center justify-center">
              <Building2 className="w-6 h-6 text-[#0B1F3B]" />
            </div>
            <span className="text-xl font-extrabold text-white" style={{ fontFamily: 'Manrope' }}>
              MyProjectBidder
            </span>
          </div>

          <nav className="space-y-2">
            <Link
              to="/business"
              className="sidebar-link active flex items-center gap-3 px-4 py-3 rounded-lg text-white"
            >
              <Home className="w-5 h-5" />
              Dashboard
            </Link>
            <Link
              to="/business/profile"
              className="sidebar-link flex items-center gap-3 px-4 py-3 rounded-lg text-slate-300 hover:text-white"
            >
              <Settings className="w-5 h-5" />
              {t("nav.businessProfile")}
            </Link>
            <Link
              to="/business/analytics"
              className="sidebar-link flex items-center gap-3 px-4 py-3 rounded-lg text-slate-300 hover:text-white"
              data-testid="analytics-sidebar-link"
            >
              <BarChart3 className="w-5 h-5" />
              {t("analytics.title")}
            </Link>
          </nav>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-white/10">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                  <User className="w-5 h-5 text-[#D4AF37]" />
                </div>
              )}
              <div className="overflow-hidden">
                <p className="text-white font-medium text-sm truncate">{user?.name}</p>
                <p className="text-slate-400 text-xs truncate">{profile?.business_name}</p>
              </div>
            </div>
            <NotificationBell />
          </div>
          <Link
            to="/settings"
            className="sidebar-link flex items-center gap-3 px-4 py-3 mb-2 rounded-lg text-slate-300 hover:text-white"
            data-testid="settings-link"
          >
            <Globe className="w-5 h-5" />
            {t("common.settings")}
          </Link>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-slate-300 hover:text-white hover:bg-white/10"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {t("common.signOut")}
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="md:hidden bg-[#0B1F3B] text-white p-4 sticky top-0 z-20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#D4AF37] flex items-center justify-center">
              <Building2 className="w-5 h-5 text-[#0B1F3B]" />
            </div>
            <span className="font-bold">MyProjectBidder</span>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/business/profile")}
              className="text-white hover:bg-white/10"
            >
              <Settings className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-white hover:bg-white/10"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="dashboard-content p-4 md:p-8" data-testid="business-dashboard">
        {/* Approval Status Banner */}
        {profile && !profile.is_approved && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-yellow-800">{t("business.pendingApproval")}</p>
              <p className="text-sm text-yellow-700">
                {t("business.pendingApprovalDesc")}
              </p>
            </div>
          </div>
        )}

        {/* Stripe Connect Banner */}
        {profile?.is_approved && connectStatus && !connectStatus.onboarding_complete && (
          <div className="mb-6 p-4 bg-[#0B1F3B] rounded-lg flex items-center justify-between gap-4" data-testid="connect-banner">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#D4AF37] flex items-center justify-center flex-shrink-0">
                <CreditCard className="w-5 h-5 text-[#0B1F3B]" />
              </div>
              <div>
                <p className="font-semibold text-white">{t("business.completePaymentSetup")}</p>
                <p className="text-sm text-slate-300">{t("business.connectBankAccount")}</p>
              </div>
            </div>
            <Button
              onClick={handleConnectOnboard}
              disabled={connectLoading}
              className="bg-[#D4AF37] hover:bg-[#C4A030] text-[#0B1F3B] font-bold flex-shrink-0"
              data-testid="connect-onboard-btn"
            >
              {connectLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ExternalLink className="w-4 h-4 mr-2" />}
              {t("business.setUpPayouts")}
            </Button>
          </div>
        )}
        {profile?.is_approved && connectStatus?.onboarding_complete && (
          <div className="mb-6 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-3" data-testid="connect-complete">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
            <p className="text-sm font-medium text-emerald-700">{t("business.paymentSetupComplete")}</p>
          </div>
        )}

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-xl md:text-3xl font-extrabold text-[#0B1F3B]" style={{ fontFamily: 'Manrope' }}>
            {profile?.business_name || "Business Dashboard"}
          </h1>
          <p className="text-slate-600 mt-1 text-sm md:text-base">
            {profile?.is_approved
              ? t("business.viewAndManage")
              : t("business.completeProfile")}
          </p>
        </div>

        {profile?.is_approved && (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
              <div className="stats-card p-3 md:p-4">
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="icon-wrapper bg-blue-100 w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center">
                    <FileText className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xl md:text-2xl font-bold text-[#0B1F3B]">{pagination.total}</p>
                    <p className="text-xs md:text-sm text-slate-500">{t("business.available")}</p>
                  </div>
                </div>
              </div>
              <div className="stats-card p-3 md:p-4">
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="icon-wrapper bg-green-100 w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center">
                    <Send className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xl md:text-2xl font-bold text-[#0B1F3B]">{estimates.length}</p>
                    <p className="text-xs md:text-sm text-slate-500">{t("business.sent")}</p>
                  </div>
                </div>
              </div>
              <div className="stats-card p-3 md:p-4">
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="icon-wrapper bg-yellow-100 w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center">
                    <Clock className="w-4 h-4 md:w-5 md:h-5 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-xl md:text-2xl font-bold text-[#0B1F3B]">
                      {estimates.filter((e) => e.status === "submitted").length}
                    </p>
                    <p className="text-xs md:text-sm text-slate-500">{t("business.pending")}</p>
                  </div>
                </div>
              </div>
              <div className="stats-card p-3 md:p-4">
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="icon-wrapper bg-purple-100 w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center">
                    <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xl md:text-2xl font-bold text-[#0B1F3B]">
                      {estimates.filter((e) => e.status === "accepted").length}
                    </p>
                    <p className="text-xs md:text-sm text-slate-500">{t("business.accepted")}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-4 mb-4 border-b border-slate-200 overflow-x-auto">
              <button
                onClick={() => setActiveTab("projects")}
                className={`pb-3 px-1 font-medium whitespace-nowrap transition-colors duration-200 ${
                  activeTab === "projects"
                    ? "text-[#0B1F3B] border-b-2 border-[#D4AF37]"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {t("business.availableProjects")} ({pagination.total})
              </button>
              <button
                onClick={() => setActiveTab("estimates")}
                className={`pb-3 px-1 font-medium whitespace-nowrap transition-colors duration-200 ${
                  activeTab === "estimates"
                    ? "text-[#0B1F3B] border-b-2 border-[#D4AF37]"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {t("business.myEstimates")} ({estimates.length})
              </button>
            </div>

            {/* Projects Tab */}
            {activeTab === "projects" && (
              <>
                {/* Search & Filters */}
                <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
                  <div className="flex flex-col md:flex-row gap-3">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Search projects..."
                        value={filters.search}
                        onChange={(e) => handleFilterChange("search", e.target.value)}
                        className="pl-9 h-10"
                        data-testid="search-input"
                      />
                    </div>

                    <Button
                      variant="outline"
                      onClick={() => setShowFilters(!showFilters)}
                      className="md:hidden"
                    >
                      <SlidersHorizontal className="w-4 h-4 mr-2" />
                      Filters
                    </Button>

                    <div className={`flex flex-col md:flex-row gap-3 ${showFilters ? 'block' : 'hidden md:flex'}`}>
                      <Select value={filters.category_id} onValueChange={(v) => handleFilterChange("category_id", v)}>
                        <SelectTrigger className="w-full md:w-[150px] h-10">
                          <SelectValue placeholder="Category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Categories</SelectItem>
                          {categories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <Input
                        placeholder="City"
                        value={filters.city}
                        onChange={(e) => handleFilterChange("city", e.target.value)}
                        className="w-full md:w-[120px] h-10"
                      />

                      <Select value={`${filters.sort_by}-${filters.sort_order}`} onValueChange={(v) => {
                        const [sortBy, sortOrder] = v.split("-");
                        setFilters(prev => ({ ...prev, sort_by: sortBy, sort_order: sortOrder }));
                      }}>
                        <SelectTrigger className="w-full md:w-[150px] h-10">
                          <ArrowUpDown className="w-4 h-4 mr-2" />
                          <SelectValue placeholder="Sort" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="created_at-desc">Newest</SelectItem>
                          <SelectItem value="created_at-asc">Oldest</SelectItem>
                          <SelectItem value="title-asc">Title A-Z</SelectItem>
                        </SelectContent>
                      </Select>

                      <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg">
                        <Switch
                          id="hide-submitted"
                          checked={filters.hide_submitted}
                          onCheckedChange={(v) => handleFilterChange("hide_submitted", v)}
                        />
                        <Label htmlFor="hide-submitted" className="text-sm whitespace-nowrap cursor-pointer">
                          {filters.hide_submitted ? <EyeOff className="w-4 h-4 inline mr-1" /> : <Eye className="w-4 h-4 inline mr-1" />}
                          Hide submitted
                        </Label>
                      </div>

                      {hasActiveFilters && (
                        <Button variant="ghost" onClick={clearFilters} className="h-10">
                          <X className="w-4 h-4 mr-1" />
                          Clear
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Projects List */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  {projects.length === 0 ? (
                    <div className="p-8 md:p-16 text-center">
                      <FileText className="w-12 md:w-16 h-12 md:h-16 mx-auto mb-4 text-slate-300" />
                      <h3 className="text-lg font-semibold text-[#0B1F3B] mb-2">
                        {hasActiveFilters ? t("business.noMatchingProjects") : t("business.noAvailableProjects")}
                      </h3>
                      <p className="text-slate-500">
                        {hasActiveFilters ? t("business.adjustFilters") : t("business.checkBackSoon")}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="divide-y divide-slate-100">
                        {projects.map((project) => (
                          <Link
                            key={project.id}
                            to={`/business/project/${project.id}`}
                            className="block p-4 md:p-6 hover:bg-slate-50 transition-colors duration-200"
                            data-testid={`project-${project.id}`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-2 mb-2">
                                  <h3 className="font-semibold text-[#0B1F3B] truncate">{project.title}</h3>
                                  <Badge className="bg-blue-100 text-blue-700 border border-blue-200 text-xs">
                                    {project.category_name}
                                  </Badge>
                                  {project.has_submitted_estimate && (
                                    <Badge className="bg-green-100 text-green-700 border border-green-200 text-xs">
                                      <Send className="w-3 h-3 mr-1" />
                                      Sent
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-slate-600 line-clamp-2 mb-2">{project.description}</p>
                                <div className="flex flex-wrap items-center gap-3 text-xs md:text-sm text-slate-500">
                                  <span className="flex items-center gap-1">
                                    <MapPin className="w-3 h-3 md:w-4 md:h-4" />
                                    {project.city}, {project.zip_code}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3 md:w-4 md:h-4" />
                                    {formatDate(project.created_at)}
                                  </span>
                                  <span className="hidden sm:flex items-center gap-1">
                                    <User className="w-3 h-3 md:w-4 md:h-4" />
                                    {project.customer_name}
                                  </span>
                                </div>
                              </div>
                              <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                            </div>
                          </Link>
                        ))}
                      </div>

                      {/* Pagination */}
                      {pagination.total_pages > 1 && (
                        <div className="p-4 border-t border-slate-100 flex items-center justify-between">
                          <p className="text-sm text-slate-500">
                            Page {pagination.page} of {pagination.total_pages}
                          </p>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={pagination.page <= 1}
                              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                            >
                              <ChevronLeft className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={pagination.page >= pagination.total_pages}
                              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                            >
                              <ChevronRight className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </>
            )}

            {/* Estimates Tab */}
            {activeTab === "estimates" && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                {estimates.length === 0 ? (
                  <div className="p-8 md:p-16 text-center">
                    <Send className="w-12 md:w-16 h-12 md:h-16 mx-auto mb-4 text-slate-300" />
                    <h3 className="text-lg font-semibold text-[#0B1F3B] mb-2">{t("business.noEstimatesYet")}</h3>
                    <p className="text-slate-500">{t("business.browseProjects")}</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {estimates.map((estimate) => (
                      <div key={estimate.id} className="p-4 md:p-6" data-testid={`estimate-${estimate.id}`}>
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <p className="text-lg font-bold text-[#0B1F3B]">
                                {formatCurrency(estimate.amount)}
                              </p>
                              <Badge
                                className={`text-xs ${
                                  estimate.status === "accepted"
                                    ? "bg-green-100 text-green-700 border-green-200"
                                    : estimate.status === "rejected"
                                    ? "bg-red-100 text-red-700 border-red-200"
                                    : "bg-yellow-100 text-yellow-700 border-yellow-200"
                                } border`}
                              >
                                {estimate.status}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-600 line-clamp-2">{estimate.description}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs md:text-sm text-slate-500">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3 md:w-4 md:h-4" />
                                {estimate.timeline}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3 md:w-4 md:h-4" />
                                {formatDate(estimate.created_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Pending Approval State */}
        {profile && !profile.is_approved && (
          <div className="bg-white rounded-xl border border-slate-200 p-6 md:p-8 text-center">
            <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-4">
              <Clock className="w-7 h-7 md:w-8 md:h-8 text-yellow-600" />
            </div>
            <h3 className="text-lg md:text-xl font-bold text-[#0B1F3B] mb-2">{t("business.awaitingApproval")}</h3>
            <p className="text-slate-600 mb-6 max-w-md mx-auto text-sm md:text-base">
              {t("business.awaitingApprovalDesc")}
            </p>
            <Button
              onClick={() => navigate("/business/profile")}
              variant="outline"
              className="border-[#0B1F3B] text-[#0B1F3B]"
            >
              <Settings className="w-4 h-4 mr-2" />
              {t("business.viewProfile")}
            </Button>
          </div>
        )}
      </main>
      {showTour && <OnboardingTour onComplete={() => setShowTour(false)} />}
    </div>
  );
}
